"use client"

import { useState } from "react"
import { useLandRisk } from "@/lib/landrisk-context"
import type { UserProfile } from "@/lib/types"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Card, CardContent } from "@/components/ui/card"
import { Mail, Linkedin, Facebook } from "lucide-react"

interface ProfileModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function ProfileModal({ open, onOpenChange }: ProfileModalProps) {
  const { setUserProfile } = useLandRisk()
  const [email, setEmail] = useState("")
  const [name, setName] = useState("")
  const [isEmailMode, setIsEmailMode] = useState(false)

  const handleSocialLogin = (provider: "google" | "linkedin" | "facebook") => {
    // In a real implementation, this would use OAuth
    const profile: UserProfile = {
      id: `${provider}-${Date.now()}`,
      email: `user@${provider}.com`,
      name: `${provider} User`,
      provider,
      createdAt: new Date().toISOString(),
    }
    setUserProfile(profile)
    onOpenChange(false)
  }

  const handleEmailSignup = (e: React.FormEvent) => {
    e.preventDefault()
    if (!email || !name) return

    const profile: UserProfile = {
      id: `email-${Date.now()}`,
      email,
      name,
      createdAt: new Date().toISOString(),
    }
    setUserProfile(profile)
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Save Your Results</DialogTitle>
          <DialogDescription>
            Create a profile to store your historical assessments
          </DialogDescription>
        </DialogHeader>

        {!isEmailMode ? (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground text-center">
              Quick sign up with social media
            </p>
            <Button
              onClick={() => handleSocialLogin("google")}
              variant="outline"
              className="w-full gap-2"
            >
              <Mail className="size-4" />
              Continue with Gmail
            </Button>
            <Button
              onClick={() => handleSocialLogin("linkedin")}
              variant="outline"
              className="w-full gap-2"
            >
              <Linkedin className="size-4" />
              Continue with LinkedIn
            </Button>
            <Button
              onClick={() => handleSocialLogin("facebook")}
              variant="outline"
              className="w-full gap-2"
            >
              <Facebook className="size-4" />
              Continue with Facebook
            </Button>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-border" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">Or</span>
              </div>
            </div>

            <Button
              onClick={() => setIsEmailMode(true)}
              variant="outline"
              className="w-full"
            >
              Sign up with Email
            </Button>

            <Button
              onClick={() => onOpenChange(false)}
              variant="ghost"
              className="w-full"
            >
              Skip for now
            </Button>
          </div>
        ) : (
          <form onSubmit={handleEmailSignup} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Full Name</Label>
              <Input
                id="name"
                placeholder="John Doe"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <Button type="submit" className="w-full" disabled={!email || !name}>
              Create Profile
            </Button>
            <Button
              type="button"
              onClick={() => setIsEmailMode(false)}
              variant="ghost"
              className="w-full"
            >
              Back
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  )
}
